/**
 * ============================================================================
 * EquipmentTimeline — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: vertical timeline of equipment lifecycle events. Follows the
 *          SupplierDetail timeline pattern — a connecting rail on the
 *          inline-start edge, one card per event with a typed dot anchor.
 *
 * A11Y: rendered as an <ol> so the order is announced; each event card
 *  exposes the kind via aria-label and via a visible mono-font tag.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './EquipmentDetail.module.css';
import type {
  EquipmentEventKind,
  EquipmentTimelineEvent,
} from './useEquipmentDetailQuery';

const KIND_DOT: Record<EquipmentEventKind, string> = {
  CREATED: styles.dotBlue,
  URS_APPROVED: styles.dotBlue,
  DQ_APPROVED: styles.dotBlue,
  FAT_PASSED: styles.dotBlue,
  SAT_PASSED: styles.dotBlue,
  IQ_PASSED: styles.dotBlue,
  OQ_PASSED: styles.dotBlue,
  PQ_PASSED: styles.dotBlue,
  QUALIFIED: styles.dotNeutral,
  CALIBRATION_RECORDED: styles.dotInfo,
  PM_RECORDED: styles.dotInfo,
  LOCATION_CHANGED: styles.dotWarn,
  LIFECYCLE_TRANSITION: styles.dotBlue,
  RESTRICTED: styles.dotWarn,
  REQUALIFICATION_STARTED: styles.dotWarn,
  REQUALIFICATION_COMPLETED: styles.dotNeutral,
  RETIRED: styles.dotMuted,
  DECOMMISSIONED: styles.dotMuted,
  OTHER: styles.dotMuted,
};

export interface EquipmentTimelineProps {
  events: EquipmentTimelineEvent[];
}

export function EquipmentTimeline({ events }: EquipmentTimelineProps) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );

  if (events.length === 0) {
    return (
      <p className={styles.timelineEmpty}>
        {t('equipment.detail.timeline.empty', {
          defaultValue: 'No lifecycle events recorded yet.',
        })}
      </p>
    );
  }

  return (
    <ol
      className={styles.timeline}
      aria-label={t('equipment.detail.timeline.listAria', {
        defaultValue: 'Equipment lifecycle timeline',
      })}
    >
      {events.map((ev) => {
        const kindLabel = t(`equipment.detail.timeline.kind.${ev.kind}`, {
          defaultValue: ev.kind,
        });
        return (
          <li key={ev.event_id} className={styles.timelineItem}>
            <div
              className={`${styles.timelineDot} ${KIND_DOT[ev.kind]}`}
              aria-hidden="true"
            />
            <article
              className={styles.timelineCard}
              aria-label={t('equipment.detail.timeline.itemAria', {
                defaultValue: '{{kind}} on {{ts}}',
                kind: kindLabel,
                ts: dateFmt.format(new Date(ev.ts_utc)),
              })}
            >
              <header className={styles.timelineHeader}>
                <span className={styles.timelineKind}>{kindLabel}</span>
                <time className={styles.timelineTs} dateTime={ev.ts_utc}>
                  {dateFmt.format(new Date(ev.ts_utc))}
                </time>
              </header>
              <h3 className={styles.timelineTitle}>{ev.title}</h3>
              {ev.description && (
                <p className={styles.timelineDesc}>{ev.description}</p>
              )}
              {ev.actor && (
                <p className={styles.timelineActor}>
                  {t('equipment.detail.timeline.actor', {
                    defaultValue: 'By {{actor}}',
                    actor: ev.actor,
                  })}
                </p>
              )}
            </article>
          </li>
        );
      })}
    </ol>
  );
}
